/**
 * @documental
 * @export TextArea
 */

import { forwardRef } from "react";
import { withSubComponents } from "../../utilities/withSubComponents";
import { Field } from "./Field";
import { InputBaseProps } from "../InputBaseProps";
import { RefComponent } from "../RefComponent";
import { SharedProps } from "./SharedProps";
import { Input } from "./Input";
import { Placeholder } from "./Placeholder";

/**
 * The `TextArea` component properties.
 */

export interface TextAreaProps extends InputBaseProps<string | null>, SharedProps {}

/**
 * The `TextArea` component sub components.
 */
export interface TextAreaSubComponents {
  /**
   * A React Hook Form compatible TextArea Field component. For more information, see the documentation for
   * the <a href="https://react-hook-form.com/api">React Hook Form API</a>.
   */
  readonly Field: typeof Field;

  /**
   * A placeholder component for the TextArea which can be displayed while loading.
   */
  readonly Placeholder: typeof Placeholder;
}

/**
 * An input component which allows the user to enter a multiple lines of plain text.
 *
 * `import { TextArea } from "./TextArea";`
 *
 *
 * <strong>To use the React Hook Form version add the following use the `Field` sub component:</strong>
 *
 * `<TextArea.Field .../>`
 *
 * <strong>To use the regular version of the TextArea</strong>
 *
 * `<TextArea .../>`
 *
 * The regular version acts like a normal field, in order to make it work
 * you need to pass in a `value` and an `onChange` for it to be interactive
 *
 * ```typescript
 * const [value, onChange] = useSimpleInputState<string | null | undefined>("");

   const handleChange = (prev: any) => {
      onChange(prev, value);
   }

   return (
     <TextArea
       label="None RHF TextArea"
       placeholder="Sample Text"
       onChange={handleChange}
       value={value}
       instructions="Sample instructions"
       {...props}
     />
   )
  ```
 */
export const TextArea = withSubComponents<RefComponent<HTMLTextAreaElement, TextAreaProps>, TextAreaSubComponents>(
  {
    Field,
    Placeholder,
  },
  forwardRef<HTMLTextAreaElement, TextAreaProps>(function TextArea(props, ref) {
    return <Input {...props} ref={ref} />;
  }),
);
